import * as RadixSelect from '@radix-ui/react-select';
import { ReactNode, useEffect, useState } from 'react';

import { IconChevronDown as ChevronDown, IconChevronUp as ChevronUp } from '../../core';

import {
  FieldTitle,
  SelectContent,
  SelectTrigger,
  SelectTriggerWithTitleContainer,
} from './Select.styled';

type SelectData = {
  label: ReactNode | string;
  icon?: ReactNode;
  value: string;
  // Still selectable, but drawn without the hover, selected and pointer states of a button.
  plain?: boolean;
};

type SelectTriggerFiledProps = {
  data: SelectData[];
  isOpen: boolean;
  activeItem?: SelectData;
  title?: string;
};

type SelectProps = {
  data: SelectData[];
  title?: string;
  defaultValue?: string;
  className?: string;
  onSelect?: (value: string) => void;
  setStaticDefaultOption?: boolean;
  paddingZero?: boolean;
  // Shown on the trigger instead of an option, and not listed as one.
  triggerLabel?: ReactNode;
};

const getActiveItem = (isFixedDefault: boolean, data: SelectData[], selectedValue: string) => {
  if (isFixedDefault) return data.at(0);

  return data.find((item) => item.value === selectedValue);
};

const SelectTriggerFiled = ({ title, activeItem, data, isOpen }: SelectTriggerFiledProps) => {
  if (!title)
    return (
      <SelectTrigger className="options-container">
        <div className="select-selected-option">
          {activeItem?.icon && (
            <div className="select-selected-option__icon">{activeItem?.icon}</div>
          )}{' '}
          {activeItem?.label || data[0].label}
        </div>
        <div className="select-icon">{isOpen ? <ChevronUp /> : <ChevronDown />}</div>
      </SelectTrigger>
    );

  return (
    <SelectTrigger className="options-container" style={{ padding: '4px 14px', height: 'auto' }}>
      <SelectTriggerWithTitleContainer>
        <FieldTitle>{title}</FieldTitle>
        <div className="select-selected-option">
          {activeItem?.icon && (
            <div className="select-selected-option__icon">{activeItem?.icon}</div>
          )}{' '}
          {activeItem?.label || data[0].label}
        </div>
      </SelectTriggerWithTitleContainer>
      <div className="select-icon">{isOpen ? <ChevronUp /> : <ChevronDown />}</div>
    </SelectTrigger>
  );
};

export const Select = ({
  className,
  data,
  onSelect,
  defaultValue,
  title,
  setStaticDefaultOption,
  paddingZero,
  triggerLabel,
}: SelectProps) => {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  // Radix focuses the selected option on open, which draws the focus ring even after a mouse
  // click; plain options hide that ring unless the list is being used from the keyboard.
  const [inputModality, setInputModality] = useState<'pointer' | 'keyboard'>('keyboard');

  const activeItem =
    triggerLabel === undefined
      ? getActiveItem(Boolean(setStaticDefaultOption), data, value)
      : { label: triggerLabel, value: '' };

  useEffect(() => {
    if (setStaticDefaultOption) return;

    setValue(defaultValue || '');
  }, [defaultValue, setStaticDefaultOption]);

  return (
    <div
      className={className}
      onPointerDownCapture={() => setInputModality('pointer')}
      onKeyDownCapture={() => setInputModality('keyboard')}
    >
      <RadixSelect.Root
        open={open}
        value={value}
        onOpenChange={setOpen}
        onValueChange={(val) => {
          if (onSelect) onSelect(val);
          setValue(val);
        }}
      >
        <SelectTriggerFiled data={data} activeItem={activeItem} isOpen={open} title={title} />
        <SelectContent
          paddingZero={paddingZero}
          style={{ zIndex: 1111, position: 'relative' }}
          className="select-options"
          position="popper"
          data-input-modality={inputModality}
        >
          {data.map((item) => (
            <RadixSelect.Item
              key={item.value}
              value={item.value}
              data-plain={item.plain ? '' : undefined}
            >
              {item.icon && <div className="select-options-item__icon">{item.icon}</div>}{' '}
              <div className="select-options-item__label">{item.label}</div>
            </RadixSelect.Item>
          ))}
        </SelectContent>
      </RadixSelect.Root>
    </div>
  );
};
